/*****************************************************************************
 *
 * Redux action creators. These trigger changes to the global redux store
 * At this point, all calls to these actions come from the App.js container
 *
 *****************************************************************************/

import _ from 'lodash'

import * as types from './types'
import * as selectors from './selectors'

import analytics from './analytics'

import NationalMapExport from "../components/Charts/NationalMap/NationalMapExport";
import StateMapExport from "../components/Charts/StateMap/StateMapExport";

import * as COLUMNS from '../utilities/columns'
import {TEXT} from '../utilities/strings'

import {
  createCsv,
  saveTextFile,
  saveSvgTextImage
} from "./export";

import {
  getExportColumns,
  getStateSectionTitle,
  getCountySectionTitle,
  getSectionFootnote,
  getStateExportTitle,
  getCountyExportTitle,
} from "../utilities/utilities";


/*****************************************************************************
 *
 * SIMPLE STORE ACTIONS
 *
 *****************************************************************************/

/**
 * Sets the store.loading boolean value
 *
 * @param loading
 * @returns {{payload: {loading: *}, type: string}}
 */
export const setLoading = (loading) => ({
  type: types.SET_LOADING,
  payload: {loading}
})

/**
 * Sets the store.displayType string value
 *
 * @param displayType
 * @returns {{payload: {displayType: *}, type: string}}
 */
export const setDisplayType = (displayType) => ({
  type: types.SET_DISPLAY_TYPE,
  payload: {displayType}
})

/**
 * Sets the store.summaryType string value
 *
 * @param summaryType
 * @returns {{payload: {summaryType: *}, type: string}}
 */
export const setSummaryType = (summaryType) => ({
  type: types.SET_SUMMARY_TYPE,
  payload: {summaryType}
})

/**
 * Sets the store.selectedCost string value
 *
 * @param selectedCost
 * @returns {{payload: {selectedCost: *}, type: string}}
 */
export const setSelectedCost = (selectedCost) => ({
  type: types.SET_SELECTED_COST,
  payload: {selectedCost}
})

/**
 * Sets the store.selectedStateFips number value
 *
 * @param selectedStateFips
 * @returns {{payload: {selectedStateFips: *}, type: string}}
 */
export const setSelectedStateFips = (selectedStateFips) => ({
  type: types.SET_SELECTED_STATE_FIPS,
  payload: {selectedStateFips}
})

/**
 * Sets the store.comparisonStateFips number value
 *
 * @param comparisonStateFips
 * @returns {{payload: {setComparisonStateFips: *}, type: string}}
 */
export const setComparisonStateFips = (comparisonStateFips) => ({
  type: types.SET_COMPARISON_STATE_FIPS,
  payload: {comparisonStateFips}
})

/**
 * Sets the store.selectedCountyFips number value
 *
 * @param selectedCountyFips
 * @returns {{payload: {selectedCountyFips: *}, type: string}}
 */
export const setSelectedCountyFips = (selectedCountyFips) => ({
  type: types.SET_SELECTED_COUNTY_FIPS,
  payload: {selectedCountyFips}
})

/**
 * Sets the store.comparisonCountyFips number value
 *
 * @param comparisonCountyFips
 * @returns {{payload: {comparisonCountyFips: *}, type: string}}
 */
export const setComparisonCountyFips = (comparisonCountyFips) => ({
  type: types.SET_COMPARISON_COUNTY_FIPS,
  payload: {comparisonCountyFips}
})

/**
 * Sets the store.nationalData array value
 *
 * @param nationalData
 * @returns {{payload: {nationalData: *}, type: string}}
 */
export const setNationalData = (nationalData) => ({
  type: types.SET_NATIONAL_DATA,
  payload: {nationalData}
})

/**
 * Sets the store.statesData array value
 *
 * @param statesData
 * @returns {{payload: {statesData: *}, type: string}}
 */
export const setStatesData = (statesData) => ({
  type: types.SET_STATES_DATA,
  payload: {statesData}
})

/**
 * Sets the store.countiesData array value
 *
 * @param countiesData
 * @returns {{payload: {countiesData: *}, type: string}}
 */
export const setCountiesData = (countiesData) => ({
  type: types.SET_COUNTIES_DATA,
  payload: {countiesData}
})

/*****************************************************************************
 *
 * COMBINED SELECTION ACTIONS
 *
 *****************************************************************************/

/**
 * Sets all related form store values from a single selection object
 * created when the form is submitted.
 *
 * Dispatches updateSelectedStateFips last to handle data load if necesary
 *
 * @param selections {{
 *   summaryType: string,
 *   selectedCost: string,
 *   displayType: string,
 *   selectedStateFips: number,
 *   comparisonStateFips: number,
 *   selectedCountyFips: number,
 *   comparisonCountyFips: number
 * }}
 * @returns {{payload: *, type: string}}
 */
export const updateSelections = (selections) => {
  return (dispatch, getState) => {
    const state = getState();

    // Only modify state fips values if state and selections values don't match.
    const comparisonStateFipsEqual = selections.comparisonStateFips === state.comparisonStateFips;
    const selectedStateFipsEqual = selections.selectedStateFips === state.selectedStateFips;

    // Dispatch individual actions for selections
    dispatch(setSummaryType(selections.summaryType))
    dispatch(setSelectedCost(selections.selectedCost))
    dispatch(setDisplayType(selections.displayType))

    if (!comparisonStateFipsEqual) {
      dispatch(setComparisonStateFips(selections.comparisonStateFips))
    }

    dispatch(setSelectedCountyFips(selections.selectedCountyFips))
    dispatch(setComparisonCountyFips(selections.comparisonCountyFips))

    // Dispatched last because it overwrites others if changed
    if (!selectedStateFipsEqual) {
      dispatch(updateSelectedStateFips(selections.selectedStateFips))
    }
  }
}

/**
 * Handles dispatching related events when selectedStateFips value changes.
 *
 * Will manage the selected and comparison values to prevent duplication
 * Will manage loading counties data for selected state
 * Will clear selected county values
 *
 * Triggers analytics interaction update FILTER
 *
 * @param stateFips
 * @returns {function(...[*]=)}
 */
export const updateSelectedStateFips = (stateFips) => {
  return (dispatch, getStore) => {
    const currentSelectedStateFips = selectors.getSelectedStateFips(getStore())
    const currentComparisonStateFips = selectors.getComparisonStateFips(getStore())

    // Clear the comparison state if we are setting it as selected
    if (stateFips === currentComparisonStateFips) {
      dispatch(setComparisonStateFips(null))
    }

    // If the state selection changed
    if (stateFips !== currentSelectedStateFips) {

      // Clear existing county information
      dispatch(setCountiesData([]))
      dispatch(setComparisonCountyFips(null))
      dispatch(setSelectedCountyFips(null))

      // Request counties data for new state
      dispatch(requestCountiesData(stateFips))
    }

    // Set the new selected state fips value
    dispatch(setSelectedStateFips(stateFips))

    // Analytics tracked
    analytics.trackFiltersUpdate(getStore())
  }
}

/**
 * Handles dispatching related events when comparisonStateFips value changes.
 *
 * Will manage the selected and comparison values to prevent duplication
 *
 * Triggers analytics interaction update FILTER
 *
 * @param stateFips
 * @returns {function(...[*]=)}
 */
export const updateComparisonStateFips = (stateFips) => {
  return (dispatch, getStore) => {
    const currentSelectedStateFips = selectors.getSelectedStateFips(getStore())

    // Clear the selected state if we are setting it as comparison
    if (stateFips === currentSelectedStateFips) {
      dispatch(setSelectedStateFips(null))
    }

    // Set the new comparison state fips value
    dispatch(setComparisonStateFips(stateFips))

    // Analytics tracked
    analytics.trackFiltersUpdate(getStore())
  }
}

/**
 * Handles dispatching related events when selectedCountyFips value changes.
 *
 * Will manage the selected and comparison values to prevent duplication
 *
 * Triggers analytics interaction update FILTER
 *
 * @param countyFips
 * @returns {function(...[*]=)}
 */
export const updateSelectedCountyFips = (countyFips) => {
  return (dispatch, getStore) => {
    const currentComparisonCountyFips = selectors.getComparisonCountyFips(getStore())

    // Clear the comparison county if we are setting it as selected
    if (countyFips === currentComparisonCountyFips) {
      dispatch(setComparisonCountyFips(null))
    }

    // Set the new selected county fips value
    dispatch(setSelectedCountyFips(countyFips))

    // Analytics tracked
    analytics.trackFiltersUpdate(getStore())
  }
}

/**
 * Handles dispatching related events when comparisonCountyFips value changes.
 *
 * Will manage the selected and comparison values to prevent duplication
 *
 * Triggers analytics interaction update FILTER
 *
 * @param countyFips
 * @returns {function(...[*]=)}
 */
export const updateComparisonCountyFips = (countyFips) => {
  return (dispatch, getStore) => {
    const currentSelectedCountyFips = selectors.getSelectedCountyFips(getStore())

    // Clear the selected county if we are setting it as comparison
    if (countyFips === currentSelectedCountyFips) {
      dispatch(setSelectedCountyFips(null))
    }

    // Set the new comparison county fips value
    dispatch(setComparisonCountyFips(countyFips))

    // Analytics tracked
    analytics.trackFiltersUpdate(getStore())
  }
}

/*****************************************************************************
 *
 * EXPORT ACTIONS
 *
 *****************************************************************************/

/**
 * Renders a national map with the current store values off screen
 * and converts the svg text to a canvas to export a .png image for download.
 *
 * Triggers analytics interaction update EXPORT with sec=STATE and ext=PNG
 *
 * @returns {function(*, *): void}
 */
export const exportStatesImage = () => {
  return (dispatch, getStore) => {
    const selectedCost = selectors.getSelectedCost(getStore())
    const statesData = selectors.getStatesData(getStore())

    const filename = _.kebabCase(getStateExportTitle(selectedCost)) + '.png'
    const svgText = NationalMapExport.renderSvgText(selectedCost, statesData, 850, 750)

    saveSvgTextImage(svgText, filename, 850, 750)

    // Analytics tracked for image export
    analytics.trackDataExport('STATE', 'PNG', getStore())
  }
}

/**
 * Renders a national map with the current store values off screen
 * and saves the svg text to a .svg file for download.
 *
 * Triggers analytics interaction update EXPORT with sec=STATE and ext=SVG
 *
 * @returns {function(*, *): void}
 */
export const exportStatesVector = () => {
  return (dispatch, getStore) => {
    const selectedCost = selectors.getSelectedCost(getStore())
    const statesData = selectors.getStatesData(getStore())

    const filename = _.kebabCase(getStateExportTitle(selectedCost)) + '.svg'
    const svgText = NationalMapExport.renderSvgText(selectedCost, statesData, 850, 750)

    saveTextFile(svgText, filename)

    // Analytics tracked for image export
    analytics.trackDataExport('STATE', 'SVG', getStore())
  }
}

/**
 * Renders a state map with the current store values off screen
 * and converts the svg text to a canvas to export a .png image for download.
 *
 * Triggers analytics interaction update EXPORT with sec=COUNTY and ext=PNG
 *
 * @returns {function(*, *): void}
 */
export const exportCountiesImage = () => {
  return (dispatch, getStore) => {
    const selectedState = selectors.getSelectedState(getStore())
    const selectedCost = selectors.getSelectedCost(getStore())
    const countiesData = selectors.getCountiesData(getStore())

    const filename = _.kebabCase(_.get(selectedState, 'State') + ' ' + getCountyExportTitle(selectedCost)) + '.png'
    const svgText = StateMapExport.renderSvgText(selectedCost, selectedState, countiesData, 850, 750)

    saveSvgTextImage(svgText, filename, 850, 750)

    // Analytics tracked for image export
    analytics.trackDataExport('COUNTY', 'PNG', getStore())
  }
}

/**
 * Renders a state map with the current store values off screen
 * and saves the svg text to a .svg file for download.
 *
 * Triggers analytics interaction update EXPORT with sec=COUNTY and ext=SVG
 *
 * @returns {function(*, *): void}
 */
export const exportCountiesVector = () => {
  return (dispatch, getStore) => {
    const selectedState = selectors.getSelectedState(getStore())
    const selectedCost = selectors.getSelectedCost(getStore())
    const countiesData = selectors.getCountiesData(getStore())

    const filename = _.kebabCase(_.get(selectedState, 'State') + ' ' + getCountyExportTitle(selectedCost)) + '.svg'
    const svgText = StateMapExport.renderSvgText(selectedCost, selectedState, countiesData, 850, 750)

    saveTextFile(svgText, filename)

    // Analytics tracked for image export
    analytics.trackDataExport('COUNTY', 'SVG', getStore())
  }
}

/**
 * Converts the current states store values into text .csv formatted file for download.
 *
 * Triggers analytics interaction update EXPORT with sec=STATE and ext=CSV
 *
 * @returns {function(*, *): void}
 */
export const exportStatesData = () => {
  return (dispatch, getStore) => {
    const selectedCost = selectors.getSelectedCost(getStore())
    const nationalData = selectors.getNationalData(getStore())
    const statesData = selectors.getStatesData(getStore())
    const exportData = _.concat(statesData, nationalData)

    const columns = getExportColumns(selectedCost)
    const title = getStateSectionTitle(selectedCost)
    const footnote = getSectionFootnote(selectedCost)

    const filename = _.kebabCase(getStateExportTitle(selectedCost)) + '.csv'
    const csvText = createCsv(exportData, columns, [title, footnote])

    saveTextFile(csvText, filename)

    // Analytics tracked for data export
    analytics.trackDataExport('STATE', 'CSV', getStore())
  }
}

/**
 * Converts the counties current store values into text .csv formatted file for download.
 *
 * Triggers analytics interaction update EXPORT with sec=COUNTY and ext=CSV
 *
 * @returns {function(*, *): void}
 */
export const exportCountiesData = () => {
  return (dispatch, getStore) => {
    const selectedCost = selectors.getSelectedCost(getStore())
    const selectedState = selectors.getSelectedState(getStore())
    const nationalData = selectors.getNationalData(getStore())
    const countiesData = selectors.getCountiesData(getStore())
    const exportData = _.concat(countiesData, selectedState, nationalData)

    const columns = getExportColumns(selectedCost)
    const title = getCountySectionTitle(selectedCost)
    const footnote = getSectionFootnote(selectedCost)

    const filename = _.kebabCase(_.get(selectedState, 'State') + ' ' + getCountyExportTitle(selectedCost)) + '.csv'
    const csvText = createCsv(exportData, columns, [title, footnote])

    saveTextFile(csvText, filename)

    // Analytics tracked for data export
    analytics.trackDataExport('COUNTY', 'CSV', getStore())
  }
}

/**
 * Converts the current summary store values into text .csv formatted file for download.
 *
 * Triggers analytics interaction update EXPORT with sec=SUMMARY and ext=CSV
 *
 * @returns {function(*, *): void}
 */
export const exportSummaryData = () => {
  return (dispatch, getStore) => {
    const nationalData = selectors.getNationalData(getStore())
    const selectedState = selectors.getSelectedState(getStore())
    const comparisonState = selectors.getComparisonState(getStore())
    const selectedCounty = selectors.getSelectedCounty(getStore())
    const comparisonCounty = selectors.getComparisonCounty(getStore())
    const exportData = _.concat(nationalData, selectedState, comparisonState, selectedCounty, comparisonCounty)

    const columns = COLUMNS.SUMMARY_EXPORT_COLUMNS
    const title = TEXT.summary_title
    const footnote = TEXT.footnote_total

    const filename = `OUDC-Summary-Export.csv`
    const csvText = createCsv(exportData, columns, [title, footnote])

    saveTextFile(csvText, filename)

    // Analytics tracked for data export
    analytics.trackDataExport('SUMMARY', 'CSV', getStore())
  }
}

/*****************************************************************************
 *
 * DATA REQUEST ACTIONS
 *
 *****************************************************************************/

/**
 * Request the national data to be loaded and set to the store when complete
 *
 * @returns {function(*, *, *): Promise<*>}
 */
export const requestNationalData = () => {
  return (dispatch, getStore, api) => {

    dispatch(setLoading(true))

    return api.getNationalData()
      .then(result => {
        dispatch(setNationalData(result))
      })
      .catch(() => {
        dispatch(setNationalData([]))
      })
      .finally(() => {
        dispatch(setLoading(false))
      })
  }
}

/**
 * Request the states data to be loaded and set to the store when complete
 *
 * @returns {function(*, *, *): Promise<*>}
 */
export const requestStatesData = () => {
  return (dispatch, getStore, api) => {

    dispatch(setLoading(true))

    return api.getStatesData()
      .then(result => {
        dispatch(setStatesData(result))
      })
      .catch(() => {
        dispatch(setStatesData([]))
      })
      .finally(() => {
        dispatch(setLoading(false))
      })
  }
}

/**
 * Request the counties data to be loaded and set to the store when complete
 *
 * @param stateFips
 * @returns {function(*, *, *): Promise<unknown>}
 */
export const requestCountiesData = (stateFips) => {
  return (dispatch, getStore, api) => {

    if (stateFips) {
      dispatch(setLoading(true))

      return api.getCountyData(stateFips)
        .then(result => {
          dispatch(setCountiesData(result))
        })
        .catch(() => {
          dispatch(setCountiesData([]))
        })
        .finally(() => {
          dispatch(setLoading(false))
        })
    }
    else {
      return dispatch(setCountiesData([]))
    }
  }
}
